import { clearAll } from './cacheStore';

/**
 * Derruba o cache de GETs depois de qualquer request não-GET.
 *
 * Cego de propósito (limpa tudo, não só o recurso mutado): a relação entre
 * "o que mutei" e "que lista cacheada isso invalida" não é óbvia nem local.
 * `PUT /bet-houses/:id/params` muda o `usable` de TODO creator daquela casa;
 * um `PATCH /bet-houses/:id` que troque `generationMode` faz o mesmo. Exigir
 * que cada tela lembrasse disso já falhou uma vez — três telas mutavam
 * utilizabilidade e só uma invalidava. Cachear aqui é otimização de leitura
 * repetida (listas de referência em toda montagem de página), não de escrita:
 * pagar um refetch por mutação é barato e remove a classe de bug inteira.
 *
 * Limpa mesmo quando a resposta é erro: um 5xx pode vir DEPOIS do write ter
 * commitado (ex.: geração de link que grava e depois falha no encurtador), e
 * manter cache velho nesse caso é pior que um refetch desnecessário.
 */
function invalidateOnMutation(method: string | undefined): void {
  if ((method ?? 'GET').toUpperCase() !== 'GET') clearAll();
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  headers.set('Content-Type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...options, headers, credentials: 'include' });
  } finally {
    // No `finally`, não depois do await: uma falha de rede tem a mesma
    // propriedade de um 5xx — o write pode ter commitado e a resposta ter se
    // perdido no caminho. Invalidar só no caminho feliz deixaria cache velho
    // exatamente no cenário em que menos dá pra confiar nele.
    invalidateOnMutation(options.method);
  }
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const b = body as Record<string, unknown> | null;
    const bodyMsg =
      b && typeof b === 'object'
        // `message` (frase legível) tem prioridade sobre `error` (slug de
        // máquina, ex: "invalid_creator") quando o backend manda os dois —
        // ver routes/links.ts, routes/apiKeys.ts.
        ? (('message' in b && String(b.message)) || ('error' in b && String(b.error)) || ('detail' in b && String(b.detail)) || null)
        : null;
    throw new ApiError(res.status, body, bodyMsg ?? `Erro ${res.status}`);
  }
  return body as T;
}

/** Variante de `api()` pra multipart/form-data (upload de arquivo). Não
 *  reusa `api()` porque ele força `Content-Type: application/json` — pra
 *  FormData o browser precisa setar o próprio `Content-Type` (com o
 *  boundary do multipart), então aqui o header não é tocado. */
export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData,
  options: { method?: string } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: options.method ?? 'POST',
      body: formData,
      credentials: 'include',
    });
  } finally {
    invalidateOnMutation(options.method ?? 'POST');
  }
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const b = body as Record<string, unknown> | null;
    const bodyMsg =
      b && typeof b === 'object'
        ? (('message' in b && String(b.message)) || ('error' in b && String(b.error)) || ('detail' in b && String(b.detail)) || null)
        : null;
    throw new ApiError(res.status, body, bodyMsg ?? `Erro ${res.status}`);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}
